import { useState } from 'react';
import { Plus, Search, Filter, CheckSquare, Circle, CheckCircle2, Clock, AlertCircle } from 'lucide-react';
import { useTasks, useCompleteTask, type TaskFilters } from '../hooks/useTasks';
import { Badge } from '../components/shared/Badge';
import { EmptyState } from '../components/shared/EmptyState';
import { cn } from '../lib/cn';

interface TasksViewProps {
  onNavigate: (view: string, params?: Record<string, string>) => void;
}

const priorityColors: Record<string, string> = {
  low: 'gray',
  medium: 'blue',
  high: 'orange',
  urgent: 'red',
};

const statusOptions = ['', 'pending', 'in_progress', 'completed', 'cancelled'];
const priorityOptions = ['', 'low', 'medium', 'high', 'urgent'];

function isOverdue(task: { dueDate?: string; status: string }): boolean {
  if (!task.dueDate || task.status === 'completed' || task.status === 'cancelled') return false;
  return new Date(task.dueDate) < new Date();
}

function getDateGroup(dueDate?: string): string {
  if (!dueDate) return 'No Due Date';
  const due = new Date(dueDate);
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const tomorrow = new Date(today.getTime() + 86_400_000);
  const nextWeek = new Date(today.getTime() + 7 * 86_400_000);
  const dueDay = new Date(due.getFullYear(), due.getMonth(), due.getDate());

  if (dueDay < today) return 'Overdue';
  if (dueDay.getTime() === today.getTime()) return 'Today';
  if (dueDay.getTime() === tomorrow.getTime()) return 'Tomorrow';
  if (dueDay < nextWeek) return 'This Week';
  return 'Later';
}

const groupOrder = ['Overdue', 'Today', 'Tomorrow', 'This Week', 'Later', 'No Due Date'];

export function TasksView({ onNavigate }: TasksViewProps) {
  const [tab, setTab] = useState<'my' | 'all'>('my');
  const [filters, setFilters] = useState<TaskFilters>({ limit: 100 });
  const [searchText, setSearchText] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [expandedTaskId, setExpandedTaskId] = useState<string | null>(null);

  const queryFilters: TaskFilters = {
    ...filters,
    search: searchText || undefined,
    // 'my' tab would filter by current user — server handles based on auth
    ...(tab === 'my' ? { assigneeId: 'me' } : {}),
  };

  const { data, isLoading, error } = useTasks(queryFilters);
  const completeTask = useCompleteTask();

  function handleComplete(taskId: string) {
    completeTask.mutate(taskId);
  }

  // Group tasks
  const tasks = data?.tasks ?? [];
  const grouped = new Map<string, typeof tasks>();
  for (const task of tasks) {
    const group = getDateGroup(task.dueDate);
    if (!grouped.has(group)) grouped.set(group, []);
    grouped.get(group)!.push(task);
  }

  return (
    <div className="flex-1 overflow-y-auto">
      {/* Header */}
      <div className="page-header">
        <div>
          <h1 className="text-xl font-bold text-slate-900">Tasks</h1>
          <p className="mt-0.5 text-sm text-slate-500">
            {data ? `${data.total} tasks` : 'Manage your tasks'}
          </p>
        </div>
        <button className="btn-primary">
          <Plus className="h-4 w-4" />
          New Task
        </button>
      </div>

      {/* Tabs + Filters */}
      <div className="border-b border-surface-200 bg-white px-6 py-3">
        <div className="flex items-center gap-4">
          <div className="flex rounded-lg border border-surface-200 p-0.5">
            <button
              className={cn(
                'rounded-md px-3 py-1 text-sm font-medium transition-colors',
                tab === 'my' ? 'bg-brand-50 text-brand-700' : 'text-slate-600 hover:text-slate-900',
              )}
              onClick={() => setTab('my')}
            >
              My Tasks
            </button>
            <button
              className={cn(
                'rounded-md px-3 py-1 text-sm font-medium transition-colors',
                tab === 'all' ? 'bg-brand-50 text-brand-700' : 'text-slate-600 hover:text-slate-900',
              )}
              onClick={() => setTab('all')}
            >
              All Tasks
            </button>
          </div>

          <form onSubmit={(e) => e.preventDefault()} className="relative flex-1 max-w-sm">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              placeholder="Search tasks..."
              className="input pl-9"
            />
          </form>

          <button
            onClick={() => setShowFilters(!showFilters)}
            className={cn('btn-secondary btn-sm', showFilters && 'bg-brand-50 border-brand-200 text-brand-700')}
          >
            <Filter className="h-3.5 w-3.5" />
            Filters
          </button>
        </div>

        {showFilters && (
          <div className="mt-3 flex items-center gap-3">
            <div>
              <label className="label mb-1">Status</label>
              <select
                className="input py-1.5"
                value={filters.status ?? ''}
                onChange={(e) => setFilters((f) => ({ ...f, status: e.target.value || undefined }))}
              >
                <option value="">All</option>
                {statusOptions.filter(Boolean).map((s) => (
                  <option key={s} value={s}>{s.replace('_', ' ')}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="label mb-1">Priority</label>
              <select
                className="input py-1.5"
                value={filters.priority ?? ''}
                onChange={(e) => setFilters((f) => ({ ...f, priority: e.target.value || undefined }))}
              >
                <option value="">All</option>
                {priorityOptions.filter(Boolean).map((p) => (
                  <option key={p} value={p}>{p}</option>
                ))}
              </select>
            </div>
          </div>
        )}
      </div>

      {/* Task List */}
      <div className="p-6">
        {error ? (
          <div className="card p-8 text-center text-sm text-red-600">
            Failed to load tasks. Please try again.
          </div>
        ) : isLoading ? (
          <div className="card divide-y divide-surface-100">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="flex items-center gap-4 px-4 py-3">
                <div className="h-5 w-5 animate-pulse rounded-full bg-surface-200" />
                <div className="flex-1">
                  <div className="h-4 w-48 animate-pulse rounded bg-surface-200" />
                  <div className="mt-1 h-3 w-24 animate-pulse rounded bg-surface-100" />
                </div>
              </div>
            ))}
          </div>
        ) : tasks.length > 0 ? (
          <div className="space-y-6">
            {groupOrder.map((groupName) => {
              const groupTasks = grouped.get(groupName);
              if (!groupTasks || groupTasks.length === 0) return null;
              return (
                <div key={groupName}>
                  <h3 className={cn(
                    'mb-2 text-xs font-semibold uppercase tracking-wider',
                    groupName === 'Overdue' ? 'text-red-500' : 'text-slate-400',
                  )}>
                    {groupName} ({groupTasks.length})
                  </h3>
                  <div className="card divide-y divide-surface-100">
                    {groupTasks.map((task) => {
                      const overdue = isOverdue(task);
                      const expanded = expandedTaskId === task.id;

                      return (
                        <div key={task.id}>
                          <div
                            className={cn(
                              'flex items-center gap-3 px-4 py-3 cursor-pointer hover:bg-surface-50',
                              overdue && 'bg-red-50/50',
                            )}
                            onClick={() => setExpandedTaskId(expanded ? null : task.id)}
                          >
                            {/* Complete checkbox */}
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                if (task.status !== 'completed') handleComplete(task.id);
                              }}
                              className="shrink-0"
                            >
                              {task.status === 'completed' ? (
                                <CheckCircle2 className="h-5 w-5 text-green-500" />
                              ) : (
                                <Circle className={cn('h-5 w-5', overdue ? 'text-red-400' : 'text-slate-300')} />
                              )}
                            </button>

                            {/* Title */}
                            <div className="min-w-0 flex-1">
                              <span
                                className={cn(
                                  'text-sm font-medium',
                                  task.status === 'completed' ? 'text-slate-400 line-through' : 'text-slate-900',
                                )}
                              >
                                {task.title}
                              </span>
                              {task.entityName && (
                                <span className="ml-2 text-xs text-slate-400">{task.entityName}</span>
                              )}
                            </div>

                            {/* Due date */}
                            {task.dueDate && (
                              <span className={cn(
                                'shrink-0 flex items-center gap-1 text-xs',
                                overdue ? 'text-red-500 font-medium' : 'text-slate-400',
                              )}>
                                {overdue && <AlertCircle className="h-3 w-3" />}
                                <Clock className="h-3 w-3" />
                                {new Date(task.dueDate).toLocaleDateString()}
                              </span>
                            )}

                            {/* Priority */}
                            <Badge color={priorityColors[task.priority] ?? 'gray'} size="sm">
                              {task.priority}
                            </Badge>

                            {/* Assignee */}
                            {task.assigneeName && (
                              <span className="shrink-0 text-xs text-slate-500">
                                {task.assigneeName}
                              </span>
                            )}
                          </div>

                          {/* Expanded details */}
                          {expanded && task.description && (
                            <div className="border-t border-surface-100 bg-surface-50 px-4 py-3">
                              <p className="text-sm text-slate-600 whitespace-pre-wrap">{task.description}</p>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <EmptyState
            icon={CheckSquare}
            title="No tasks"
            description="You are all caught up! Create a task to track your to-dos."
            actionLabel="Add Task"
          />
        )}
      </div>
    </div>
  );
}
